import {
  ApolloQueryResult,
  FetchPolicy,
  LazyQueryExecFunction,
  useLazyQuery
} from '@apollo/client'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { createContext, useContext, useLayoutEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DraftQuestionnaireV2QueryVariables } from '../../data/gql-gen/questionnaire/graphql'
import {
  QUESTIONNAIRE,
  QuestionnaireData
} from '../../data/gql/questionnaire/queries'
import { LoggerErrorType } from '../../data/logger'
import {
  DraftQuestionnaire,
  DraftQuestionnaireEntry
} from '../../data/model/questionnaire'
import { flattenEntries } from '../../modules/Questionnaire/Questionnaire.utils'
import { captureApolloError } from '../../utils/HelperFunctions'
import { useOptionalWaveId } from '../useOptionalWaveId'
import { useProjectId } from '../useProjectId'
import { useSurveyId } from '../useSurveyId'

interface UseGetDraftQuestionnaire {
  draftQuestionnaire: DraftQuestionnaire | undefined
  questionnaireLoading: boolean
  draftQuestionnaireEntries: DraftQuestionnaireEntry[]
  flattenedEntries: DraftQuestionnaireEntry[]
  fetchQuestionnaire: LazyQueryExecFunction<
    QuestionnaireData,
    DraftQuestionnaireV2QueryVariables
  >
  refetchQuestionnaire: (
    variables?: Partial<DraftQuestionnaireV2QueryVariables> | undefined
  ) => Promise<ApolloQueryResult<QuestionnaireData>>
}

export const DraftQuestionnaireContext =
  createContext<UseGetDraftQuestionnaire | null>(null)

/**
 * @deprecated You should use useQuery/useFragment with the *specific* query you want to use instead of this hook.
 */
export const useGetDraftQuestionnaireValue = (): UseGetDraftQuestionnaire => {
  let navigate: ReturnType<typeof useNavigate> | undefined
  let enqueueSnackbar:
    | ReturnType<typeof useSnackbar>['enqueueSnackbar']
    | undefined

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate()
  } catch {
    navigate = undefined
  }

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    enqueueSnackbar = useSnackbar().enqueueSnackbar
  } catch {
    enqueueSnackbar = undefined
  }

  const [
    fetchQuestionnaire,
    {
      data: questionnaireData,
      loading: questionnaireLoading,
      refetch: refetchQuestionnaire
    }
  ] = useLazyQuery<QuestionnaireData, DraftQuestionnaireV2QueryVariables>(
    QUESTIONNAIRE,
    {
      context: { clientName: 'questionnaire' },
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloQuery,
          'draftQuestionnaire',
          error
        )

        // Check if questionnaire doesn't exist
        const isQuestionnaireNotFound =
          error.message.includes('not found') ||
          error.message.includes('does not exist') ||
          error.graphQLErrors.some(
            (err) =>
              err.message.includes('not found') ||
              err.message.includes('does not exist')
          )

        if (isQuestionnaireNotFound) {
          if (enqueueSnackbar) {
            enqueueSnackbar(
              'The questionnaire could not be found. Returning to projects page.',
              {
                variant: SnackbarVariant.Error
              }
            )
          }
          if (navigate) {
            navigate('/projects')
          }
        }
      }
    }
  )

  const draftQuestionnaire = questionnaireData?.draftQuestionnaire
  // TODO flatten + check if it doesn't break anything after backend fixes for sections are done
  const draftQuestionnaireEntries = useMemo(() => {
    return [
      ...(draftQuestionnaire?.audienceEntries || []),
      ...(draftQuestionnaire?.entries || [])
    ]
  }, [draftQuestionnaire?.audienceEntries, draftQuestionnaire?.entries])
  const flattenedEntries = useMemo(() => {
    return flattenEntries(draftQuestionnaire?.entries ?? [])
  }, [draftQuestionnaire?.entries])

  return useMemo(
    () => ({
      draftQuestionnaireEntries,
      draftQuestionnaire,
      flattenedEntries,
      questionnaireLoading,
      refetchQuestionnaire,
      fetchQuestionnaire
    }),
    [
      draftQuestionnaire,
      draftQuestionnaireEntries,
      flattenedEntries,
      questionnaireLoading,
      refetchQuestionnaire,
      fetchQuestionnaire
    ]
  )
}

/**
 * @deprecated You should use useQuery/useFragment with the *specific* query you want to use instead of this hook.
 */
function useGetDraftQuestionnaire(
  fetchPolicy: FetchPolicy = 'cache-first'
): UseGetDraftQuestionnaire {
  const value = useContext(DraftQuestionnaireContext)
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  if (!value) {
    throw new Error('Must wrap in QuestionnaireContextProvider')
  }

  const { fetchQuestionnaire } = value

  // The questionnaire is readable in any survey state, so the fetch is
  // deliberately NOT gated on useIsAudienceReadOnly: read-only surfaces (e.g.
  // the multi-market Confirm page's translation warnings after a market
  // launched) still need the entries, and editors gate writes themselves.
  // This also means read-only single-survey surfaces that previously skipped
  // the fetch (e.g. a live survey's audience-page breadcrumb) now issue it —
  // one extra read, accepted so every consumer sees consistent data.
  useLayoutEffect(() => {
    if (surveyId) {
      fetchQuestionnaire({
        variables: { projectId, surveyId, waveId },
        fetchPolicy
      })
    }
  }, [projectId, surveyId, waveId, fetchPolicy, fetchQuestionnaire])

  return value
}

export default useGetDraftQuestionnaire
